import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { usePerformanceBenchmarkReport } from "@/hooks/reports/performance-benchmark-report.hook";
import { cn } from "@/lib/utils";
import {
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from "chart.js";
import { FC, HTMLAttributes } from "react";
import { Radar } from "react-chartjs-2";
import { useParams } from "react-router-dom";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

type PerformanceByDimensionCardProps = HTMLAttributes<HTMLDivElement>;

const PerformanceByDimensionCard: FC<PerformanceByDimensionCardProps> = ({
  className,
  ...props
}) => {
  const { data: userProfile } = useCurrentUserProfile();

  const { quizId } = useParams();

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: quizId || "",
  });

  const { data: report, isLoading: isReportLoading } =
    usePerformanceBenchmarkReport(
      {
        companyId: userProfile?.companyId || "",
        editionId: quiz?.edition.id || "",
        group: "SECTOR",
      },
      !!quiz?.edition.id,
    );

  const reportData = {
    labels: report?.result.dimensionResults.map((item) => item.dimension.name),
    groupScores: report?.groupAverageResult.dimensionResults.map(
      (item) => item.result.score,
    ),
    companyScores: report?.result.dimensionResults.map(
      (item) => item.result.score,
    ),
  };

  const data = {
    labels: reportData.labels, // Etiquetas de las esquinas
    datasets: [
      {
        label: "Puntaje de tu empresa",
        data: reportData.companyScores, // Datos aleatorios para la empresa
        backgroundColor: "rgba(255, 185, 44, 0.2)", // Color de relleno (transparente)
        borderColor: "#FFB92C", // Color del borde
        borderWidth: 2, // Grosor del borde
        pointBackgroundColor: "#FFB92C", // Color de los puntos
        pointStyle: "circle", // Estilo del punto (círculo)
        pointRadius: 6, // Radio de los puntos en el gráfico
        pointBorderColor: "#FFB92C", // Color del borde del punto
        pointHoverBackgroundColor: "#FFFFFF", // Fondo blanco en hover
        pointHoverBorderColor: "#FFB92C", // Borde del punto en hover
      },
      {
        label: "Puntaje Promedio",
        data: reportData.groupScores, // Datos aleatorios para el promedio
        backgroundColor: "rgba(97, 92, 183, 0.2)", // Color de relleno (transparente)
        borderColor: "#615CB7", // Color del borde
        borderWidth: 2, // Grosor del borde
        pointBackgroundColor: "#615CB7", // Color de los puntos
        pointStyle: "circle", // Estilo del punto (círculo)
        pointRadius: 6, // Radio de los puntos en el gráfico
        pointBorderColor: "#615CB7", // Color del borde del punto
        pointHoverBackgroundColor: "#FFFFFF", // Fondo blanco en hover
        pointHoverBorderColor: "#615CB7", // Borde del punto en hover
      },
    ],
  };

  const options: ChartOptions<"radar"> = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true, // Usa el estilo de punto definido
          boxWidth: 10, // Ancho de los puntos en la leyenda
          boxHeight: 10, // Altura de los puntos en la leyenda
          padding: 15, // Espaciado entre leyendas
          color: "#000", // Color del texto de la leyenda
        },
      },
      tooltip: {
        enabled: true, // Activa los tooltips
      },
    },
    scales: {
      r: {
        angleLines: {
          color: "#E0E0E0", // Color de las líneas que conectan las esquinas
        },
        grid: {
          color: "#D3D3D3", // Color de la grilla
        },
        ticks: {
          display: false,
          stepSize: 20, // Incrementos en el radar
          color: "#666", // Color de los valores numéricos
        },
        suggestedMin: 0, // Mínimo valor sugerido
        suggestedMax: 100, // Máximo valor sugerido
      },
    },
  };

  return (
    <Card className={cn(className, "rounded-xl px-5 py-3")} {...props}>
      <p className="mb-1 mt-2 text-xl font-bold text-[#13171B]">
        Desempeño por dimensión
      </p>

      <p className="mb-6 text-sm font-light text-[#8090A3]">
        Identifica en qué áreas has obtenido un mejor desempeño y oportunidades
        de mejora.
      </p>

      {report && (
        <div className="flex max-h-[400px] items-center justify-center">
          <Radar data={data} options={options} />
        </div>
      )}

      {isReportLoading && (
        <Skeleton style={{ width: "100%", height: "200px" }} />
      )}
    </Card>
  );
};

export default PerformanceByDimensionCard;
